/**
 * Native Web MIDI Access Manager with Safari / unsupported fallback
 * Allows binding an external MIDIInput or scanning and connecting all local inputs
 */

import { MidiDeviceItem } from '../types/midi';

export type NoteOnCallback = (pitch: number, velocity: number, channel: number, timestampMs: number) => void;
export type NoteOffCallback = (pitch: number, channel: number, timestampMs: number) => void;
export type DeviceChangeCallback = (devices: MidiDeviceItem[], activeDeviceId: string | null) => void;
export type MidiActivityCallback = (activity: {
  pitch: number;
  velocity: number;
  channel: number;
  type: 'on' | 'off';
  timestamp: number;
}) => void;

export class MidiAccessManager {
  private midiAccess: MIDIAccess | null = null;
  private currentInput: MIDIInput | null = null;
  private isExternalInput = false;
  private activeInputId: string | null = 'all'; // Default to 'all' (Omni mode) for plug-and-play
  private boundInputs: Set<MIDIInput> = new Set();
  private lastRunningStatus = 0;

  private onNoteOnListeners: Set<NoteOnCallback> = new Set();
  private onNoteOffListeners: Set<NoteOffCallback> = new Set();
  private onDeviceChangeListeners: Set<DeviceChangeCallback> = new Set();
  private onActivityListeners: Set<MidiActivityCallback> = new Set();

  public isSupported(): boolean {
    return typeof navigator !== 'undefined' && typeof navigator.requestMIDIAccess === 'function';
  }

  /**
   * Set an externally provided MIDIInput (e.g. from Chord Notator's useMidiHandlers.ts)
   */
  public setExternalInput(input: MIDIInput | null): void {
    if (input) {
      this.unbindAllInputs();
      this.currentInput = input;
      this.isExternalInput = true;
      this.activeInputId = input.id;
      this.bindInput(input);
    } else {
      if (this.isExternalInput) {
        this.unbindAllInputs();
        this.currentInput = null;
        this.isExternalInput = false;
        this.activeInputId = 'all';
        this.rebindInputs();
      }
    }
    this.notifyDeviceChanges();
  }

  /**
   * Initialize local Web MIDI Access
   */
  public async initialize(): Promise<{ success: boolean; error?: string }> {
    if (this.isExternalInput && this.currentInput) {
      return { success: true };
    }

    if (!this.isSupported()) {
      return {
        success: false,
        error: 'Web MIDI API is not supported in this browser (e.g. Safari without extension). You can still play and test via the virtual piano keyboard or mouse.',
      };
    }

    try {
      this.midiAccess = await navigator.requestMIDIAccess({ sysex: false });

      this.midiAccess.onstatechange = () => {
        this.rebindInputs();
        this.notifyDeviceChanges();
      };

      // Connect to available inputs
      this.rebindInputs();
      this.notifyDeviceChanges();

      return { success: true };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      return {
        success: false,
        error: `MIDI permission denied or unavailable: ${msg}`,
      };
    }
  }

  public getInputs(): MidiDeviceItem[] {
    if (this.isExternalInput && this.currentInput) {
      return [
        {
          id: this.currentInput.id,
          name: this.currentInput.name || 'External MIDI Input (Host)',
          manufacturer: this.currentInput.manufacturer ?? undefined,
          state: this.currentInput.state || 'connected',
        },
      ];
    }

    if (!this.midiAccess) return [];

    const list: MidiDeviceItem[] = [];
    const entries = Array.from(this.midiAccess.inputs.values());

    if (entries.length > 0) {
      list.push({
        id: 'all',
        name: 'All MIDI Inputs (Omni - Recommended)',
        manufacturer: 'Universal',
        state: 'connected',
      });
    }

    for (const entry of entries) {
      list.push({
        id: entry.id,
        name: entry.name || `MIDI Device ${entry.id}`,
        manufacturer: entry.manufacturer ?? undefined,
        state: entry.state,
      });
    }

    return list;
  }

  public getActiveInputId(): string | null {
    if (this.isExternalInput && this.currentInput) {
      return this.currentInput.id;
    }
    return this.activeInputId;
  }

  public getActiveInputName(): string {
    if (this.isExternalInput && this.currentInput) {
      return this.currentInput.name || 'External MIDI Input';
    }
    if (!this.midiAccess) return 'No MIDI';

    const inputs = Array.from(this.midiAccess.inputs.values());
    if (inputs.length === 0) return 'No MIDI Devices';

    if (this.activeInputId === 'all') {
      if (inputs.length === 1) {
        return inputs[0].name || 'MIDI Keyboard';
      }
      return `All MIDI Inputs (${inputs.length})`;
    }

    const selected = inputs.find((i) => i.id === this.activeInputId);
    return selected?.name || 'Selected MIDI Input';
  }

  public selectInput(id: string): void {
    if (this.isExternalInput) return;
    this.activeInputId = id;
    this.rebindInputs();
    this.notifyDeviceChanges();
  }

  public onNoteOn(cb: NoteOnCallback): () => void {
    this.onNoteOnListeners.add(cb);
    return () => this.onNoteOnListeners.delete(cb);
  }

  public onNoteOff(cb: NoteOffCallback): () => void {
    this.onNoteOffListeners.add(cb);
    return () => this.onNoteOffListeners.delete(cb);
  }

  public onDeviceChange(cb: DeviceChangeCallback): () => void {
    this.onDeviceChangeListeners.add(cb);
    return () => this.onDeviceChangeListeners.delete(cb);
  }

  public onActivity(cb: MidiActivityCallback): () => void {
    this.onActivityListeners.add(cb);
    return () => this.onActivityListeners.delete(cb);
  }

  /**
   * Dispatches Note On from virtual UI keyboard or external test triggers
   */
  public triggerVirtualNoteOn(pitch: number, velocity: number = 100, channel = 1): void {
    const timestamp = performance.now();
    this.dispatchNoteOn(pitch, velocity, channel, timestamp);
  }

  /**
   * Dispatches Note Off from virtual UI keyboard
   */
  public triggerVirtualNoteOff(pitch: number, channel = 1): void {
    const timestamp = performance.now();
    this.dispatchNoteOff(pitch, channel, timestamp);
  }

  private boundMidiHandler = (event: MIDIMessageEvent) => {
    this.handleMidiMessage(event);
  };

  private bindInput(input: MIDIInput): void {
    try {
      if (typeof input.open === 'function' && input.connection !== 'open') {
        input.open().catch(() => {});
      }
      input.removeEventListener('midimessage', this.boundMidiHandler as EventListener);
      input.addEventListener('midimessage', this.boundMidiHandler as EventListener);
      input.onmidimessage = this.boundMidiHandler;
      this.boundInputs.add(input);
    } catch (e) {
      console.warn('Failed to bind MIDI input:', input.name, e);
    }
  }

  private unbindInput(input: MIDIInput): void {
    try {
      input.removeEventListener('midimessage', this.boundMidiHandler as EventListener);
      input.onmidimessage = null;
      this.boundInputs.delete(input);
    } catch {
      // ignore
    }
  }

  private unbindAllInputs(): void {
    for (const input of this.boundInputs) {
      this.unbindInput(input);
    }
    this.boundInputs.clear();
  }

  private rebindInputs(): void {
    if (this.isExternalInput && this.currentInput) {
      this.bindInput(this.currentInput);
      return;
    }

    if (!this.midiAccess) return;

    const allInputs = Array.from(this.midiAccess.inputs.values());

    if (this.activeInputId === 'all' || !this.activeInputId) {
      // Bind ALL connected inputs (Omni mode)
      for (const input of allInputs) {
        if (input.state === 'connected') {
          this.bindInput(input);
        } else {
          this.unbindInput(input);
        }
      }
    } else {
      // Bind ONLY selected input
      for (const input of allInputs) {
        if (input.id === this.activeInputId && input.state === 'connected') {
          this.bindInput(input);
        } else {
          this.unbindInput(input);
        }
      }
    }
  }

  /**
   * Robust MIDI parser: handles running status, multi-packet buffers, and ignores real-time bytes
   */
  private handleMidiMessage(event: MIDIMessageEvent): void {
    if (!event.data || event.data.length === 0) return;
    const data = event.data;
    const timestamp = event.timeStamp || performance.now();

    let i = 0;
    let runningStatus = this.lastRunningStatus;

    while (i < data.length) {
      const byte = data[i];

      // Skip real-time messages (0xF8 - 0xFF: clock, active sense, etc.)
      if (byte >= 0xf8) {
        i++;
        continue;
      }

      let status: number;
      if (byte >= 0x80) {
        // New status byte
        status = byte;
        if (status < 0xf0) {
          runningStatus = status;
          this.lastRunningStatus = status;
        } else {
          runningStatus = 0;
          this.lastRunningStatus = 0;
        }
        i++;
      } else {
        // Data byte using running status
        if (!runningStatus) {
          i++;
          continue;
        }
        status = runningStatus;
      }

      const command = status >> 4;
      const channel = (status & 0x0f) + 1;

      // Note Off (0x8) or Note On (0x9)
      if (command === 0x8 || command === 0x9) {
        const pitch = byte < 0x80 ? byte : data[i++];
        const velocity = i < data.length ? data[i++] : 64;

        if (pitch !== undefined && velocity !== undefined) {
          if (command === 0x9 && velocity > 0) {
            this.dispatchNoteOn(pitch, velocity, channel, timestamp);
          } else {
            this.dispatchNoteOff(pitch, channel, timestamp);
          }
        }
      } else if (command === 0xa || command === 0xb || command === 0xe) {
        // Aftertouch, Control Change, Pitch Bend (2 data bytes)
        if (byte < 0x80) {
          i++;
        } else {
          i += 2;
        }
      } else if (command === 0xc || command === 0xd) {
        // Program Change, Channel Pressure (1 data byte)
        if (byte >= 0x80) {
          i++;
        }
      } else {
        i++;
      }
    }
  }

  private dispatchNoteOn(pitch: number, velocity: number, channel: number, timestamp: number): void {
    for (const listener of this.onNoteOnListeners) {
      try {
        listener(pitch, velocity, channel, timestamp);
      } catch (err) {
        console.error('Error in onNoteOn listener', err);
      }
    }
    for (const listener of this.onActivityListeners) {
      try {
        listener({ pitch, velocity, channel, type: 'on', timestamp });
      } catch {
        // ignore
      }
    }
  }

  private dispatchNoteOff(pitch: number, channel: number, timestamp: number): void {
    for (const listener of this.onNoteOffListeners) {
      try {
        listener(pitch, channel, timestamp);
      } catch (err) {
        console.error('Error in onNoteOff listener', err);
      }
    }
    for (const listener of this.onActivityListeners) {
      try {
        listener({ pitch, velocity: 0, channel, type: 'off', timestamp });
      } catch {
        // ignore
      }
    }
  }

  private notifyDeviceChanges(): void {
    const inputs = this.getInputs();
    const activeId = this.getActiveInputId();
    for (const listener of this.onDeviceChangeListeners) {
      listener(inputs, activeId);
    }
  }
}

export const globalMidiAccess = new MidiAccessManager();
